import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import InterviewButton from "./InterviewButton";
import BottomInterviewButton from "./BottomInterviewButton";
import InterviewButtons from "./InterviewButtons";
import { InterviewObject, MicOpts } from "@/utils/types";
import { useAppContext } from "@/appContext";
import { noFeedback } from "@/utils/utils";
import { updateTempUser } from "@/api";
import EmailModal from "./EmailModal";

interface Props {
  tabs: {
    label: string;
    route: string;
    Component: FC;
  }[];
  bottomButtons?: {
    displayPastFeedback?: boolean;
    retryClick?: () => void;
    nextClick?: () => void;
    sessionCompleteClick: () => void;
  };
  adminView?: boolean;
  isInterview?: boolean;
  micOpts?: MicOpts;
  setShowLoader?: Dispatch<SetStateAction<boolean>>;
  recognizer?: any;
}

const InterviewTabs: FC<Props> = ({
  tabs,
  bottomButtons,
  isInterview,
  micOpts,
  setShowLoader,
  adminView,
  recognizer,
}) => {
  const { saveSession, state, reducers } = useAppContext();
  const [selectedTab, setSelectedTab] = useState(
    state.showFeedback ? "feedback" : "transcript"
  );
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [ActiveTabComponent, setActiveTabComponent] = useState<FC | undefined>(
    undefined
  );

  const isSessionComplete = () => {
    for (const q of state.session) {
      if (q.feedback === "" || q.feedback === noFeedback) {
        return false;
      }
    }
    return true;
  };

  const sendFeedbackToEmail = (email: string) => {
    if (email !== "") {
      const aq: InterviewObject[] | null =
        state.profileUser?.answeredQuestions![
        state.session[state.currentQuestion].question.question
        ] || null;
      if (aq) {
        const lastAq = aq[aq.length - 1];
        updateTempUser(
          email,
          state.session[state.currentQuestion].question.question,
          lastAq
        );
      }
    }
  };

  useEffect(() => {
    let tabComponent = tabs.find((tab) => tab.route === selectedTab)?.Component;
    if (!tabComponent && tabs.length > 0) {
      const sc = "transcript";
      setSelectedTab(sc);
      tabComponent = tabs.find((tab) => tab.route === sc)?.Component;
    } else {
      if (selectedTab === "feedback") {
        reducers.setshowFeedback(true);
        reducers.setshowAns(false);
        reducers.setshowAction(false);
      } else if (selectedTab === "solution") {
        reducers.setshowAns(true);
        reducers.setshowFeedback(false);
        reducers.setshowAction(false);
      } else if (selectedTab === "action-steps") {
        reducers.setshowFeedback(false);
        reducers.setshowAns(false);
        reducers.setshowAction(true);
      } else {
        reducers.setshowFeedback(false);
        reducers.setshowAns(false);
        reducers.setshowAction(false);
      }
    }
    setActiveTabComponent(() => tabComponent || undefined);
  }, [selectedTab, tabs]);

  useEffect(() => {
    if (selectedTab === "feedback") {
      reducers.setshowFeedback(true);
      reducers.setshowAns(false);
      reducers.setshowAction(false);
    } else if (selectedTab === "solution") {
      reducers.setshowAns(true);
      reducers.setshowFeedback(false);
      reducers.setshowAction(false);
    } else if (selectedTab === "action-steps") {
      reducers.setshowFeedback(false);
      reducers.setshowAns(false);
      reducers.setshowAction(true);
    } else {
      reducers.setshowFeedback(false);
      reducers.setshowAns(false);
      reducers.setshowAction(false);
    }
  }, [state.currentQuestion]);

  return (
    <div className={"flex full-nav flex-col relative"}>
      <div className="flex pt-[50px] px-[24px] gap-4">
        {tabs.map((tab) => (
          <InterviewButton
            key={tab.label}
            isSelected={tab.route === selectedTab}
            label={tab.label}
            onClick={() => setSelectedTab(tab.route)}
          />
        ))}
      </div>
      {ActiveTabComponent && <ActiveTabComponent />}
      {isInterview && (
        <InterviewButtons
          micOpts={micOpts}
          setShowLoader={setShowLoader}
          recognizer={recognizer}
          nextClick={bottomButtons?.nextClick}
        />
      )}
      {!isInterview && bottomButtons && !adminView && (
        <div className="flex justify-between py-[30px] px-[24px] border-t w-full bg-white shadow-md bottom-0 sticky right-0">
          {!bottomButtons.displayPastFeedback && !isSessionComplete() && (
            <BottomInterviewButton
              label="Save Progress"
              onClick={() => saveSession()}
              className=""
            />
          )}
          {state.profileUser?.email.endsWith("@thedevdifference.com") &&
            state.session[state.currentQuestion].feedback !== noFeedback && (
              <BottomInterviewButton
                label="Send To Email"
                onClick={() =>
                  reducers.setoverlayComp(
                    <EmailModal
                      isOpen={true}
                      onClose={() => reducers.setoverlayComp(null)}
                      onSend={sendFeedbackToEmail}
                    />
                  )
                }
                className="ml-1"
              />
            )}

          <div className="w-[100%] flex justify-end gap-2">
            {bottomButtons.retryClick && (
              <BottomInterviewButton
                label="Retry"
                onClick={bottomButtons.retryClick}
                className=""
              />
            )}
            {!bottomButtons.displayPastFeedback &&
              bottomButtons.nextClick &&
              !isSessionComplete() && (
                <BottomInterviewButton
                  label="Next Question"
                  onClick={bottomButtons.nextClick}
                  className="bg-[#F28622] hover:bg-[#bd691c]"
                />
              )}
            {!bottomButtons.displayPastFeedback && isSessionComplete() && (
              <BottomInterviewButton
                label="Next"
                onClick={bottomButtons.sessionCompleteClick}
                className="bg-[#F28622] hover:bg-[#bd691c]"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewTabs;
